import { IonCol, IonGrid, IonRouterLink, IonRow } from "@ionic/react";
import { Reorder } from "framer-motion";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import Button from "@components/buttons/Button";
import BasicCard from "@components/cards/BasicCard";
import styles from "@components/carePlan/ExpertCarePlan.module.css";
import GoalPopover from "@components/carePlan/GoalPopover";
import IconBase from "@components/text/IconBase";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading2 from "@components/typography/Heading2";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import {
  GetJourneyWithPlanDocument,
  GetJourneyWithPlanQuery,
  GetJourneyWithPlanQueryVariables,
  GoalFragment,
  GoalPublishDocument,
  GoalPublishMutation,
  GoalPublishMutationVariables,
  GoalsReorderDocument,
  GoalsReorderMutation,
  GoalsReorderMutationVariables,
  GoalStatusEnum,
  GoalToEditModeDocument,
  GoalToEditModeMutation,
  GoalToEditModeMutationVariables
} from "@typing/Generated";
import { generateHTMLId } from "@utils/htmlUtils";
import { getAllGoals, sortByPosition } from "@utils/modelUtils";

type Journey = GetJourneyWithPlanQuery["journey"];
type GoalType = GoalFragment;

type GoalCardProps = {
  goalMenuItem: GoalMenuItem;
  handleGoalSelect: (goal: GoalType, goalMenuItem: GoalMenuItem) => void;
  journey: Journey;
};

type GoalMenuItem = {
  activeGoalId: string;
  editModeGoalId: string | null;
  id: string;
  selected: boolean;
  status: GoalStatusEnum;
};

const GoalCard = ({ goalMenuItem, handleGoalSelect, journey }: GoalCardProps) => {
  const goals = getAllGoals(journey);
  const activeGoal = goals.find(goal => goal.id === goalMenuItem.activeGoalId);
  const editModeGoal = goals?.find(goal => goal.id === goalMenuItem.editModeGoalId);

  const goal = goalMenuItem.status === GoalStatusEnum.ACTIVE ? activeGoal ?? editModeGoal : editModeGoal ?? activeGoal;

  if (!goal) return null;

  const selected = goalMenuItem.selected;

  return (
    // eslint-disable-next-line react/forbid-elements
    <IonRouterLink
      className={styles.relativeContainer}
      key={goal.id}
      onClick={() => {
        handleGoalSelect(goal, goalMenuItem);
      }}
    >
      <BasicCard
        active={selected && goalMenuItem.status !== GoalStatusEnum.EDIT_MODE}
        background={
          goalMenuItem.status === GoalStatusEnum.DRAFT
            ? "gray-100"
            : goalMenuItem.status === GoalStatusEnum.EDIT_MODE && selected
              ? "danger-300"
              : undefined
        }
        hover={selected ? "none" : "purple"}
        inactive={goal.archived || goalMenuItem.status === GoalStatusEnum.DRAFT}
        padding="small"
      >
        <IonGrid className="ion-no-padding">
          <IonRow className="ion-align-items-center">
            <IonCol>
              <BodyCopy color={goalMenuItem.status === GoalStatusEnum.DRAFT ? "gray-300" : "primary"}>
                {goal.title}
              </BodyCopy>
            </IonCol>
            <IonCol size="auto">
              <div className={styles.actions}>
                <GoalPopover color={selected ? "white" : undefined} goal={goal} journey={journey} />
              </div>
            </IonCol>
          </IonRow>
        </IonGrid>
      </BasicCard>
    </IonRouterLink>
  );
};

type Props = {
  allGoals: boolean;
  archived: boolean;
  handleGoalCreate: () => void;
  handleGoalsSelect: (goal: GoalType[], allGoals: boolean) => void;
  journey: Journey;
  loading: boolean;
  selectedGoalIds: string[];
  setAllGoals: (allGoals: boolean) => void;
  setLoading: (loading: boolean) => void;
};

const ExpertGoalMenu = ({
  allGoals,
  archived,
  handleGoalCreate,
  handleGoalsSelect,
  journey,
  loading,
  selectedGoalIds,
  setAllGoals,
  setLoading
}: Props) => {
  const goals = sortByPosition(getAllGoals(journey).filter(goal => goal.archived === archived));

  const [goalsReorder] = useMutationWithValidation<GoalsReorderMutation, GoalsReorderMutationVariables>(
    GoalsReorderDocument,
    "goal"
  );

  const [publishGoal] = useMutationWithValidation<GoalPublishMutation, GoalPublishMutationVariables>(
    GoalPublishDocument,
    "goal",
    {
      update: (store, { data }) => {
        const goal = data?.goalPublish?.goal;

        if (goal) {
          store.modify({
            fields: {
              status() {
                return GoalStatusEnum.ACTIVE;
              }
            },
            id: store.identify({ ...goal })
          });
        }

        if (!goal || !goal.activeGoalId) return;
        try {
          const cacheData = store.readQuery<GetJourneyWithPlanQuery, GetJourneyWithPlanQueryVariables>({
            query: GetJourneyWithPlanDocument,
            variables: { id: journey.id }
          });
          if (cacheData) {
            const newCacheData = {
              ...cacheData,
              journey: {
                ...cacheData.journey,
                editModeGoals: cacheData.journey.editModeGoals?.filter(g => g.id !== goal.id) ?? [],
                goals: [...cacheData.journey.goals.filter(g => g.id !== goal.activeGoalId), goal]
              }
            };

            store.writeQuery<GetJourneyWithPlanQuery, GetJourneyWithPlanQueryVariables>({
              data: newCacheData,
              query: GetJourneyWithPlanDocument
            });
          }
        } catch {
          // The query will raise an error if we haven't asked for the journey's goals
        }
      }
    }
  );

  const [publishLoading, setPublishLoading] = useState(false);

  const calculateGoalMenuItems = (previousGoalMenuItems: GoalMenuItem[]) =>
    sortByPosition(goals)
      .filter(goal => !goal || !journey.editModeGoals?.some(g => g.activeGoalId === goal.id))
      .map(goal => {
        const editModeGoal = journey.editModeGoals?.find(g => goal.id === g.id);

        const editModeGoalId = editModeGoal?.id ?? null;
        const activeGoalId = editModeGoal?.activeGoalId ?? goal?.id ?? "";
        const existingMenuItem = previousGoalMenuItems.find(
          item =>
            (item.editModeGoalId && item.editModeGoalId === editModeGoalId) ||
            item.activeGoalId === activeGoalId ||
            item.activeGoalId === editModeGoalId
        );

        return {
          activeGoalId,
          editModeGoalId,
          id: existingMenuItem ? existingMenuItem.id : generateHTMLId(),
          selected:
            existingMenuItem?.selected ??
            (selectedGoalIds.some(goalId => goalId === editModeGoalId && !allGoals) ||
              selectedGoalIds.some(goalId => goalId === activeGoalId && !allGoals)) ??
            false,
          status: editModeGoal?.status ?? goal?.status ?? GoalStatusEnum.DRAFT
        };
      });

  const [goalMenuItems, setGoalMenuItems] = useState<GoalMenuItem[]>(calculateGoalMenuItems([]));

  const newReorderItemOrder = (menuItems: GoalMenuItem[]) => [
    ...menuItems
      .filter(goalMenuItem => goalMenuItem.status === GoalStatusEnum.ACTIVE)
      .map(goalMenuItem => goalMenuItem.id),
    "EDIT_MODE",
    ...menuItems
      .filter(goalMenuItem => goalMenuItem.status === GoalStatusEnum.EDIT_MODE)
      .map(goalMenuItem => goalMenuItem.id),
    "DRAFT",
    ...menuItems
      .filter(goalMenuItem => goalMenuItem.status === GoalStatusEnum.DRAFT)
      .map(goalMenuItem => goalMenuItem.id)
  ];

  const handleAllGoals = () => {
    setAllGoals(true);
    const newGoals = sortByPosition(
      goals
        .filter(g => !journey.editModeGoals?.some(goal => goal.activeGoalId === g.id))
        .concat(journey.editModeGoals ?? [])
    );

    handleGoalsSelect(newGoals, true);
    const newGoalMenuItems = goalMenuItems.map(gmi => ({ ...gmi, selected: false }));
    setGoalMenuItems(newGoalMenuItems);
  };

  const handleGoalSelect = (goal: GoalType, goalMenuItem: GoalMenuItem) => {
    if (loading) return;

    const newGoalMenuItems = goalMenuItems.map(gmi => {
      if (gmi.id === goalMenuItem.id) {
        return {
          ...gmi,
          selected: true
        };
      }
      return { ...gmi, selected: false };
    });

    setGoalMenuItems(newGoalMenuItems);
    handleGoalsSelect([goal], false);
  };

  const [reorderItemOrder, setReorderItemOrder] = useState<string[]>(newReorderItemOrder(goalMenuItems));

  useEffect(() => {
    if (publishLoading) return;

    const newGoalMenuItems = calculateGoalMenuItems(goalMenuItems);
    setGoalMenuItems(newGoalMenuItems);
    const newOrder = newReorderItemOrder(newGoalMenuItems);
    setReorderItemOrder(newOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals.length, archived]);

  const [goalToEditMode] = useMutationWithValidation<GoalToEditModeMutation, GoalToEditModeMutationVariables>(
    GoalToEditModeDocument,
    "goal"
  );

  const handleReorder = (newMenuItemIdsOrder: string[]) => {
    if (loading) return;

    const indexOfEditMode = newMenuItemIdsOrder.findIndex(id => id === "EDIT_MODE");
    const indexOfDraft = newMenuItemIdsOrder.findIndex(id => id === "DRAFT");

    const editModeMenuItemIds = newMenuItemIdsOrder.slice(indexOfEditMode + 1, indexOfDraft);

    const menuItemInEditMode = editModeMenuItemIds.find(
      menuItemId => goalMenuItems.find(gmi => gmi.id === menuItemId)?.status === GoalStatusEnum.DRAFT
    );

    if (menuItemInEditMode) {
      setReorderItemOrder([
        ...goalMenuItems
          .filter(goalMenuItem => goalMenuItem.status === GoalStatusEnum.ACTIVE)
          .map(goalMenuItem => goalMenuItem.id),
        menuItemInEditMode,
        "EDIT_MODE",
        ...goalMenuItems
          .filter(goalMenuItem => goalMenuItem.status === GoalStatusEnum.EDIT_MODE)
          .map(goalMenuItem => goalMenuItem.id),
        "DRAFT",
        ...goalMenuItems
          .filter(goalMenuItem => goalMenuItem.status === GoalStatusEnum.DRAFT)
          .map(goalMenuItem => goalMenuItem.id)
          .filter(id => id !== menuItemInEditMode)
      ]);
      return;
    }

    if (editModeMenuItemIds.some(id => goalMenuItems.find(gmi => gmi.id === id)?.status === GoalStatusEnum.DRAFT)) {
      return;
    }

    const draftMenuItemIds = newMenuItemIdsOrder.slice(indexOfDraft + 1);

    if (draftMenuItemIds.some(id => goalMenuItems.find(gmi => gmi.id === id)?.status !== GoalStatusEnum.DRAFT)) return;

    setReorderItemOrder(newMenuItemIdsOrder);
  };

  const handleApplyChanges = async () => {
    setPublishLoading(true);
    const indexOfDraft = reorderItemOrder.findIndex(goal => goal === "DRAFT");
    const indexOfEditMode = reorderItemOrder.findIndex(goal => goal === "EDIT_MODE");

    let finalMenuItems = goalMenuItems;
    const newEditModeGoals: GoalFragment[] = [];
    const goalIdsToExclude: string[] = [];

    const menuItemsToPublish = reorderItemOrder
      .slice(0, indexOfEditMode)
      .filter(id => goalMenuItems.find(gmi => gmi.id === id)?.status !== GoalStatusEnum.ACTIVE);

    const publishPromises = menuItemsToPublish.map(async menuItemId => {
      setLoading(true);
      const menuItem = goalMenuItems.find(gmi => gmi.id === menuItemId);
      const goal =
        goals.find(goal => goal.id === menuItem?.editModeGoalId) ??
        goals.find(goal => goal.id === menuItem?.activeGoalId);

      if (!goal || goal?.status === GoalStatusEnum.ACTIVE) return;

      finalMenuItems = goalMenuItems.map(goalMenuItem => {
        if (menuItem?.id === goalMenuItem.id) {
          return {
            activeGoalId: goalMenuItem.editModeGoalId ?? goalMenuItem.activeGoalId,
            editModeGoalId: null,
            id: goalMenuItem.id,
            selected: true,
            status: GoalStatusEnum.ACTIVE
          };
        }
        return { ...goalMenuItem, selected: false };
      });

      setGoalMenuItems(finalMenuItems);

      await publishGoal({ id: goal.id })
        .then(res => {
          const goal = res?.goalPublish?.goal;
          if (goal?.activeGoalId) {
            goalIdsToExclude.push(goal.activeGoalId);
          }
          if (goal) {
            handleGoalsSelect([goal], false);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    });

    const menuItemsToEditMode = reorderItemOrder
      .slice(indexOfEditMode + 1, indexOfDraft)
      .filter(id => goalMenuItems.find(gmi => gmi.id === id)?.status !== GoalStatusEnum.EDIT_MODE);

    const updatePromises = menuItemsToEditMode.map(async menuItemId => {
      const menuItem = goalMenuItems.find(gmi => gmi.id === menuItemId);
      const goal = goals.find(goal => goal.id === menuItem?.activeGoalId);
      setLoading(true);

      if (!goal || goal?.status === GoalStatusEnum.EDIT_MODE) return;

      finalMenuItems = goalMenuItems.map(goalMenuItem => {
        if (menuItem?.id === goalMenuItem.id) {
          return {
            ...goalMenuItem,
            selected: true,
            status: GoalStatusEnum.EDIT_MODE
          };
        }
        return { ...goalMenuItem, selected: false };
      });

      setGoalMenuItems(finalMenuItems);

      await goalToEditMode({ id: goal.id })
        .then(res => {
          const newGoal = res?.goalToEditMode?.journey?.editModeGoals?.find(g => g.activeGoalId === goal.id);
          const oldGoal = goal;

          finalMenuItems = goalMenuItems.map(goalMenuItem => {
            if (menuItem?.id === goalMenuItem.id) {
              return {
                ...goalMenuItem,
                activeGoalId: oldGoal?.id ?? "",
                editModeGoalId: newGoal?.id ?? null,
                selected: true,
                status: GoalStatusEnum.EDIT_MODE
              };
            }
            return { ...goalMenuItem, selected: false };
          });

          setGoalMenuItems(finalMenuItems);

          if (newGoal && oldGoal) {
            newEditModeGoals.push(newGoal);

            handleGoalsSelect([newGoal], false);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    });

    try {
      Promise.all([...publishPromises, ...updatePromises]).then(() => {
        const goalOrderForReordering = [
          ...reorderItemOrder
            .filter(menuItemId => menuItemId !== "EDIT_MODE" && menuItemId !== "DRAFT")
            .flatMap(menuItemId => {
              const goalMenuItem = finalMenuItems.find(gmi => gmi.id === menuItemId);

              if (goalMenuItem?.editModeGoalId) {
                return [goalMenuItem.editModeGoalId, goalMenuItem.activeGoalId];
              }

              return goalMenuItem?.activeGoalId ?? "";
            }),
          ...sortByPosition(journey.goals.filter(g => g.archived !== archived)).map(g => g.id),
          ...sortByPosition(journey.editModeGoals?.filter(g => g.archived !== archived) ?? []).map(g => g.id)
        ].filter((id, index, self) => index === self.findIndex(goalId => goalId === id));

        setReorderItemOrder(reorderItemOrder);

        goalsReorder({
          goalIds: goalOrderForReordering,
          journeyId: journey.id
        }).finally(() => {
          setPublishLoading(false);
        });
      });
    } catch {
      setPublishLoading(false);
    }
  };

  const handlePublishAll = () => {
    setPublishLoading(true);
    setLoading(true);
    setGoalMenuItems(menuItems =>
      menuItems.map(gmi => ({
        ...gmi,
        activeGoalId: gmi.editModeGoalId ? gmi.editModeGoalId : gmi.activeGoalId,
        status: GoalStatusEnum.ACTIVE
      }))
    );
    setReorderItemOrder(reorderItemOrder =>
      reorderItemOrder.filter(id => id !== "EDIT_MODE" && id !== "DRAFT").concat(["EDIT_MODE", "DRAFT"])
    );

    Promise.all(goals.filter(g => g.status !== GoalStatusEnum.ACTIVE).map(g => publishGoal({ id: g.id }))).finally(
      () => {
        setPublishLoading(false);
        setLoading(false);
      }
    );
  };

  return (
    <div>
      <div className={styles.expertGrid}>
        <div className={styles.cardContainer}>
          <div>
            <div className={styles.sectionHeader}>
              <div className={styles.spaceBetween} data-test="active">
                <Heading2 color="primary">
                  <FormattedMessage id="pages.carePlan.base.goals.active" />
                </Heading2>

                <Button
                  disabled={!goals.some(g => g.status !== GoalStatusEnum.ACTIVE) || publishLoading}
                  fill="clear"
                  onClick={handlePublishAll}
                >
                  <FormattedMessage id="pages.carePlan.base.goals.publishAll" />
                  <IconBase icon="check" slot="end" />
                </Button>
              </div>

              <div className={styles.allGoalsCard}>
                <ButtonLink expand onClick={handleAllGoals} stylized={false}>
                  <BasicCard active={allGoals} boxShadow hover={!allGoals ? "purple" : "none"} padding="small">
                    <div>
                      <BodyCopy color="primary">
                        <FormattedMessage id="pages.carePlan.base.goals.all" />
                      </BodyCopy>
                    </div>
                  </BasicCard>
                </ButtonLink>
              </div>
            </div>

            <Reorder.Group
              as="div"
              axis="y"
              onPointerUp={handleApplyChanges}
              onReorder={handleReorder}
              values={reorderItemOrder}
            >
              {reorderItemOrder.map(item => {
                if (item === "EDIT_MODE" || item === "DRAFT") {
                  return (
                    <Reorder.Item
                      as="div"
                      className={styles.itemSpacing}
                      data-test={item === "EDIT_MODE" ? "edit-mode" : "draft"}
                      drag={false}
                      draggable={false}
                      key={item}
                      value={item}
                    >
                      <div className={styles.sectionHeader}>
                        <Heading2 color="primary">
                          {item === "EDIT_MODE" ? (
                            <FormattedMessage id="pages.carePlan.base.goals.editMode" />
                          ) : (
                            <FormattedMessage id="pages.carePlan.base.goals.notPublished" />
                          )}
                        </Heading2>
                      </div>
                    </Reorder.Item>
                  );
                }

                const goalMenuItem = goalMenuItems.find(goalMenuItem => goalMenuItem.id === item);

                if (!goalMenuItem) return;

                return (
                  <Reorder.Item as="div" className={styles.itemSpacing} key={item} value={item}>
                    <GoalCard goalMenuItem={goalMenuItem} handleGoalSelect={handleGoalSelect} journey={journey} />
                  </Reorder.Item>
                );
              })}
            </Reorder.Group>

            <div className={styles.addGoal}>
              <Button color="primary-500" onClick={handleGoalCreate}>
                <FormattedMessage id="pages.carePlan.base.goals.addAGoal" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpertGoalMenu;
